import InhalerIcon from '@mui/icons-material/Air';
import TopicalIcon from '@mui/icons-material/Healing';
import InjectionIcon from '@mui/icons-material/LocalHospital';
import PillIcon from '@mui/icons-material/Medication';
import CapsuleIcon from '@mui/icons-material/Medication';
import SuppositoryIcon from '@mui/icons-material/Medication';
import PowderIcon from '@mui/icons-material/Medication';
import OtherIcon from '@mui/icons-material/Medication';
import LiquidIcon from '@mui/icons-material/Opacity';
import { Box, Divider, Grid, Typography, Modal } from '@mui/material';
import React from 'react';
import { ICON_BUTTON_TYPE } from '../../../constants';
import IconButton from '../../../components/IconButton';
import TextField from '../../../components/TextInput';
import SelectInput from '../../../components/SelectInput';
import Button from '../../../components/Button';
import { generateTimeList, getTimeZoneOffset, getTimeZoneOffsetInteger } from '../../../utils';
import MultipleSelectChip from '../../../components/MultiSelectInput';
import useStore from '../../../Store/Store';
import { isEmpty, map } from 'lodash';
import axios from 'axios';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { t } from 'i18next';

const MEDICATION_TYPE = [
  { name: 'Pills', unit: 'tablet', icon: <PillIcon /> },
  { name: 'Capsule', unit: 'capsule', icon: <CapsuleIcon /> },
  { name: 'Liquid', unit: 'ml', icon: <LiquidIcon /> },
  { name: 'Injection', unit: 'injection', icon: <InjectionIcon /> },
  { name: 'Topical', unit: 'application', icon: <TopicalIcon /> },
  { name: 'Inhaler', unit: 'device', icon: <InhalerIcon /> },
  { name: 'Suppository', unit: 'unit', icon: <SuppositoryIcon /> },
  { name: 'Powder', unit: 'scoop', icon: <PowderIcon /> },
  { name: 'Other', unit: 'application', icon: <OtherIcon /> },
];

const COLORS = ['Blue', 'Red', 'Yellow', 'Green', 'Orange', 'Pink', 'Purple', 'Brown', 'Black'];

const DAY_FREQUENCY = Array.from({ length: 20 }, (_, index) => index + 1);
const DOSE = Array.from({ length: 5 }, (_, index) => index + 1);

const AddMedicationModal = ({ show, onClose, refresh, recipientId }) => {
  const selectedRecipient = useStore((state) => state.selectedRecipient);
  const user = useStore((state) => state.user);
  const [caregivers, setCaregivers] = React.useState([]);
  const [state, setState] = React.useState({
    name: '',
    form: '',
    color: '',
    dose: '',
    duration: '',
    time: [],
    remindMe: '',
    assignedTo: '',
    howToTake: '',
    description: '',
    errorMessage: '',
    startDate: '',
  });

  const getUserCaregivers = React.useCallback(async () => {
    try {
      if (!isEmpty(selectedRecipient) || !isEmpty(recipientId)) {
        let crId = selectedRecipient || recipientId;
        const { data } = await axios.get(
          process.env.REACT_APP_EXPRESS_URL + `/api/canCare/caregiver/${user?._id}/userCaregivers/${crId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              authorization: localStorage.getItem('auth'),
            },
          },
        );
        const users = map(data?.data, ({ caregiverId }) => ({
          userId: caregiverId?._id,
          name: `${caregiverId?.userInfo?.firstName} ${caregiverId?.userInfo?.lastName || ''}`,
          email: caregiverId?.email,
          username: caregiverId?.username,
        }));
        setCaregivers(users);
      } else {
        const { data } = await axios.get(
          process.env.REACT_APP_EXPRESS_URL + `/api/canCare/user/${user?._id}/getCaregivers`,
          {
            headers: {
              'Content-Type': 'application/json',
              authorization: localStorage.getItem('auth'),
            },
          },
        );
        const users = map(data?.data, ({ caregiverId }) => ({
          userId: caregiverId?._id,
          name: `${caregiverId?.userInfo?.firstName} ${caregiverId?.userInfo?.lastName || ''}`,
          email: caregiverId?.email,
          username: caregiverId?.username,
        }));
        setCaregivers(users);
      }
    } catch (error) {
      console.log(error);
    }
  }, [selectedRecipient]);

  React.useEffect(() => {
    if (user) {
      getUserCaregivers();
    }
  }, [selectedRecipient, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setState((prev) => {
      return { ...prev, errorMessage: '' };
    });

    if (!state.name) {
      setState((prev) => {
        return { ...prev, errorMessage: 'Please enter medication name' };
      });
      return;
    }

    if (!state.form) {
      setState((prev) => {
        return { ...prev, errorMessage: 'Please select medication form' };
      });
      return;
    }

    if (!state.dose) {
      setState((prev) => {
        return { ...prev, errorMessage: 'Please enter medication dose' };
      });
      return;
    }

    if (state.time.length === 0) {
      setState((prev) => {
        return { ...prev, errorMessage: 'Please select at least one time' };
      });
      return;
    }

    if (!state.duration) {
      setState((prev) => {
        return { ...prev, errorMessage: 'Please enter medication duration' };
      });
      return;
    }

    if (!state.startDate) {
      setState((prev) => {
        return { ...prev, errorMessage: 'Please select start date' };
      });
      return;
    }

    // const body = {
    //   dayFrequency: 1,
    //   timeFrequency: map(state.time, (time) => ({
    //     quantity: state.dose,
    //     time,
    //   })),
    //   type: state.form,
    //   timezone: getTimeZoneOffset(),
    //   frequency: 1,
    //   duration: state.duration,
    //   assignee: state.assignedTo,
    //   utcOffset: getTimeZoneOffsetInteger(),
    //   name: state.name,
    //   howToTake: state.howToTake,
    //   color: state.color,
    //   description: state.description,
    // };

    // console.log('state =====> ', body);

    try {
      const body = {
        name: state.name,
        type: state.form,
        frequency: 1,
        dayFrequency: 1,
        weekDayFrequency: [],
        startDate: state.startDate,
        timeFrequency: map(state.time, (time) => ({
          quantity: state.dose,
          time,
        })),
        timezone: getTimeZoneOffset(),
        duration: state.duration,
        assignee: state?.assignedTo,
        color: state.color,
        howToTake: state.howToTake,
        description: state.description,
        reminder: true,
        utcOffset: getTimeZoneOffsetInteger(),
      };
      const userId = user?._id || user?.id || '';
      let crId = selectedRecipient || recipientId;
      if (!isEmpty(selectedRecipient) || !isEmpty(recipientId)) {
        await axios.post(
          `${process.env.REACT_APP_EXPRESS_URL}/api/canCare/caregiver/${userId}/userMedications/${crId}`,
          body,
          {
            headers: {
              'Content-Type': 'application/json',
              authorization: localStorage.getItem('auth'),
            },
          },
        );
      } else {
        await axios.post(`${process.env.REACT_APP_EXPRESS_URL}/api/canCare/user/${userId}/medications`, body, {
          headers: {
            'Content-Type': 'application/json',
            authorization: localStorage.getItem('auth'),
          },
        });
      }
      onClose();
      refresh();
    } catch (error) {
      setState((prev) => {
        return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong' };
      });
    }
  };

  return (
    <Modal
      open={show}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      style={{ overflow: 'auto' }}
      BackdropProps={{ onClick: () => {} }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 940,
          maxWidth: '95vw',
          p: 4,
          bgcolor: 'background.paper',
          borderRadius: 2,
          gap: 2,
          maxHeight: '80vh',
          overflow: 'auto',
          margin: '20px 0',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography variant="h5">{t('addMedications')}</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <IconButton type={ICON_BUTTON_TYPE.close} onClick={onClose} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
          <TextField
            label={t('medication.medicationName')}
            style={{ marginBottom: 0 }}
            value={state.name}
            onChange={(e) =>
              setState((prev) => {
                return { ...prev, name: e.target.value };
              })
            }
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <Typography variant="body2">{t('medication.commonForms')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {MEDICATION_TYPE.map((item, idx) => (
                <Button
                  startIcon={item.icon}
                  text={item.name}
                  key={idx}
                  bg={'light'}
                  style={{ color: state.form === item.name ? '#26796b' : '#93939c' }}
                  onClick={() =>
                    setState((prev) => {
                      return { ...prev, form: item.name };
                    })
                  }
                />
              ))}
            </Box>
          </Box>

          <TextField
            label={t('startDate')}
            style={{ marginBottom: 0 }}
            value={state.startDate}
            type="date"
            onChange={(e) =>
              setState((prev) => {
                return { ...prev, startDate: e.target.value };
              })
            }
          />

          <Grid container spacing={1}>
            <Grid item xs={4}>
              <SelectInput
                fullWidth
                label={t('colors')}
                options={COLORS}
                value={state.color}
                onChange={(e) =>
                  setState((prev) => {
                    return { ...prev, color: e.target.value };
                  })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <SelectInput
                fullWidth
                label={t('dose')}
                options={DOSE}
                value={state.dose}
                onChange={(e) =>
                  setState((prev) => {
                    return { ...prev, dose: e.target.value };
                  })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <SelectInput
                fullWidth
                label={t('howToTake')}
                options={['Before meal', 'After meal']}
                value={state.howToTake}
                onChange={(e) =>
                  setState((prev) => {
                    return { ...prev, howToTake: e.target.value };
                  })
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid item xs={4}>
              <SelectInput
                fullWidth
                label={t('duration')}
                options={DAY_FREQUENCY}
                value={state.duration}
                onChange={(e) =>
                  setState((prev) => {
                    return { ...prev, duration: e.target.value };
                  })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <MultipleSelectChip
                options={generateTimeList()}
                label={t('time')}
                inputValue={state.time}
                setInputValue={(val) =>
                  setState((prev) => {
                    return { ...prev, time: val };
                  })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <SelectInput
                fullWidth
                label={t('remindMe')}
                options={['5 mins before', '10 mins before', '20 mins before']}
                value={state.remindMe}
                onChange={(e) =>
                  setState((prev) => {
                    return { ...prev, remindMe: e.target.value };
                  })
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label" sx={{ px: 1 }}>
                  {t('assignedTo')}
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  // value={state.}
                  label={t('assignedTo')}
                  onChange={(e) => {
                    setState((prev) => {
                      return { ...prev, assignedTo: e.target.value };
                    });
                  }}
                  style={{ borderRadius: 50 }}
                >
                  {caregivers.map((item, idx) => (
                    <MenuItem key={idx} value={item.userId}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <TextField
              label={t('description')}
              multiline
              fullWidth
              style={{ marginBottom: 0 }}
              value={state.description}
              onChange={(e) =>
                setState((prev) => {
                  return { ...prev, description: e.target.value };
                })
              }
            />
          </Box>
        </Box>

        {state.errorMessage && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
            <Typography variant="body2" color="error">
              {state.errorMessage}
            </Typography>
          </Box>
        )}

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
          <Button text={t('cancel')} bg="light" onClick={onClose} />
          <Button text={t('add')} onClick={handleSubmit} />
        </Box>
      </Box>
    </Modal>
  );
};

export default AddMedicationModal;
